import { FC, ReactNode } from "react";
import { cn } from "@/lib/utils";
import type { OrderType, TradeSide } from "./useTradingViewState";

interface TradeSideToggleProps {
  value: TradeSide;
  onChange: (side: TradeSide) => void;
}

export const TradeSideToggle: FC<TradeSideToggleProps> = ({ value, onChange }) => (
  <div className="sr-segmented-control sr-segmented-control-soft grid grid-cols-2 overflow-hidden">
    {(["buy", "sell"] as const).map((side) => {
      const isActive = value === side;

      return (
        <button
          key={side}
          onClick={() => onChange(side)}
          className={cn(
            "sr-segmented-control-item px-3 py-2 text-sm font-medium transition-colors",
            isActive && side === "buy" && "rounded-l-[14px] bg-[#123B28]/70 text-sr-mint",
            isActive && side === "sell" && "rounded-r-[14px] bg-[#3A1618]/70 text-sr-salmon",
            !isActive && "text-sr-neutral-300 hover:text-white",
          )}
        >
          {side[0].toUpperCase() + side.slice(1)}
        </button>
      );
    })}
  </div>
);

interface OrderTypeTabsProps {
  value: OrderType;
  onChange: (type: OrderType) => void;
  menuSlot?: ReactNode;
  hasLiquidity: boolean;
  selectedMarket: boolean;
}

export const OrderTypeTabs: FC<OrderTypeTabsProps> = ({
  value,
  onChange,
  menuSlot,
  hasLiquidity,
  selectedMarket,
}) => (
  <div className="mb-2 flex items-center gap-4">
    {(["limit", "market"] as const).map((type) => {
      const isSelected = value === type;

      return (
        <button
          key={type}
          onClick={() => onChange(type)}
          className={cn(
            "relative py-1 text-sm font-medium capitalize transition-colors",
            isSelected
              ? "text-white after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:rounded-full after:bg-white"
              : "text-sr-neutral-500 hover:text-sr-neutral-300",
          )}
        >
          {type}
        </button>
      );
    })}

    {menuSlot}

    <div className="ml-auto flex items-center gap-3">
      {selectedMarket && !hasLiquidity && (
        <span className="text-[10px] text-sr-tangerine">
          No liquidity
        </span>
      )}
    </div>
  </div>
);

interface LimitPriceFieldProps {
  quoteLabel: string | null;
  value: string;
  onChange: (value: string) => void;
}

export const LimitPriceField: FC<LimitPriceFieldProps> = ({
  quoteLabel,
  value,
  onChange,
}) => (
  <div className="sr-panel-tinted flex items-center justify-between gap-3 rounded-[18px] px-4 py-3">
    <span className="text-sm text-sr-neutral-400">
      Limit Price {quoteLabel ? `(${quoteLabel})` : ""}
    </span>
    <div className="flex min-w-0 items-center">
      <input
        type="text"
        inputMode="decimal"
        placeholder="0.00"
        value={value}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (nextValue === "" || /^\d*\.?\d{0,6}$/.test(nextValue)) {
            onChange(nextValue);
          }
        }}
        className="min-w-0 bg-transparent text-right text-lg font-medium text-white tabular-nums outline-none placeholder:text-sr-neutral-700"
      />
    </div>
  </div>
);

interface EstimateBarProps {
  orderType: OrderType;
  maxMarketDisplay: string | null;
  spendTokenLabel: string | undefined;
  receiveAmount: string;
  estimatedReceiveLabel: string;
}

export const EstimateBar: FC<EstimateBarProps> = ({
  orderType,
  maxMarketDisplay,
  spendTokenLabel,
  receiveAmount,
  estimatedReceiveLabel,
}) => (
  <div className="flex items-center justify-between gap-4 text-sm text-sr-neutral-500">
    {orderType === "market" && maxMarketDisplay && spendTokenLabel ? (
      <span className="tabular-nums">
        Max market order: {maxMarketDisplay} {spendTokenLabel}
      </span>
    ) : (
      <span />
    )}
    <span className="text-right tabular-nums">
      {receiveAmount ? `≈ ${receiveAmount} ${estimatedReceiveLabel}` : `≈ 0 ${estimatedReceiveLabel}`}
    </span>
  </div>
);

interface SummaryRow {
  label: string;
  value: string;
  emphasized?: boolean;
}

interface TradeSummaryProps {
  rows: SummaryRow[];
  accentTextClass: string;
}

export const TradeSummary: FC<TradeSummaryProps> = ({ rows, accentTextClass }) => (
  <div className="flex flex-col gap-3 pt-1">
    {rows.map((row) => (
      <div key={row.label} className="flex items-center justify-between gap-4 text-[13px]">
        <span className={row.emphasized ? "text-white" : "text-sr-neutral-400"}>
          {row.label}
        </span>
        <span className={row.emphasized ? cn("font-medium tabular-nums", accentTextClass) : "text-white tabular-nums"}>
          {row.value}
        </span>
      </div>
    ))}
  </div>
);

